import React, { useState, useEffect, useContext, useRef } from 'react';
import CommentTable from './VendorComments';
import DeleteModal from '../../components/modals/DeleteModal';
import { VendorContext } from '../../context/VendorProvider';
import archiveIcon from '../../assets/icons/archive.svg';
import saveIcon from '../../assets/icons/save.svg';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';
import { WaveformLoaderVendors } from '../../components/inputs/WaveformLoaderVendors';
import { UserContext } from '../../context/UserProvider';
import DualVendorSearch from '../../components/inputs/DualVendorSearch';
import VendorModal from '../../components/modals/VendorModal';
import VendorFiles from './VendorFiles';
import { FaEdit } from 'react-icons/fa';
import { FiLock, FiUnlock } from 'react-icons/fi';

export default function VendorForm({ vendor = null, onVendorDataChange, vendorLinkDisabled }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const {
    getModalFeedTypes, getModalVendorTypes, getVendor, archiveVendor, updateVendor, createVendor, selectedVendor,
    setSelectedVendor, handleVendorClick, callVendorForRefresh, setCallVendorForRefresh
  } = useContext(VendorContext);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isVendorLocked, setIsVendorLocked] = useState(!!vendor?.rowKey);
  const [isVendorCodeLocked, setIsVendorCodeLocked] = useState(!!vendor?.rowKey);
  const [isSaved, setIsSaved] = useState(false);
  const currentDate = new Date().toISOString().split('T')[0];

  const initialState = {
    delayProfile: vendor?.delayProfile || 0,
    activeSites: vendor?.activeSites || 0,
    feedType: vendor?.feedType || '',
    hasReciprocal: vendor?.hasReciprocal || false,
    inactiveSites: vendor?.inactiveSites || 0,
    licensee: vendor?.licensee || '',
    rowKey: vendor?.rowKey || undefined,
    status: vendor?.status !== undefined ? vendor.status : true,
    reciprocalAllowed: vendor?.reciprocalAllowed || false,
    startDate: vendor?.startDate || currentDate,
    endDate: vendor?.endDate || '',
    totalSites: vendor?.totalSites || 0,
    vendorCode: vendor?.vendorCode || '',
    vendorType: vendor?.vendorType || '',
    consumers: vendor?.consumers || [],
    provider: vendor?.provider || null,
    isConsumer: vendor?.isConsumer || false,
    isProvider: vendor?.isProvider || false,
    providerDetails: vendor?.providerDetails || null,
  };

  const [form, setForm] = useState(initialState);
  const initialFormRef = useRef(initialState);

  const [vendorTypes, setVendorTypes] = useState([]);
  const [feedTypes, setFeedTypes] = useState([]);
  const [showLocalSuccess, setShowLocalSuccess] = useState(false);
  const [showLocalError, setShowLocalError] = useState(false);
  const [localText, setLocalText] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1280);
  const [initialLoading, setInitialLoading] = useState(true);
  const [linkedGroup, setLinkedGroup] = useState([]);
  const { darkMode } = useContext(UserContext);
  const [isProviderOpen, setIsProviderOpen] = useState(false);

  useEffect(() => {
    if (initialFormRef.current !== null) {
      setIsFormChanged(
        JSON.stringify(form) !== JSON.stringify(initialFormRef.current)
      );
    }
  }, [form]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 700);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1280);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isLargeScreen]);

  const closeNotification = () => {
    setShowLocalSuccess(false);
    setShowLocalError(false);
  };

  const handleRefreshYes = async () => {
    setCallVendorForRefresh(false);

    const updatedVendor = await getVendor(vendor.rowKey);

    if (updatedVendor) {
      const {
        delayProfile,
        activeSites,
        feedType,
        hasReciprocal,
        inactiveSites,
        licensee,
        rowKey,
        status,
        reciprocalAllowed,
        startDate,
        endDate,
        totalSites,
        vendorCode,
        vendorType,
        consumers,
        provider,
        isConsumer,
        isProvider,
        providerDetails,
        ...vendorData
      } = updatedVendor;

      setSelectedVendor((prevVendor) => ({
        ...prevVendor,
        ...vendorData,
        delayProfile: delayProfile || 0,
        activeSites: activeSites || 0,
        feedType: feedType || '',
        hasReciprocal: hasReciprocal || false,
        inactiveSites: inactiveSites || 0,
        licensee: licensee || '',
        rowKey: rowKey,
        status: status !== undefined ? status : true,
        reciprocalAllowed: reciprocalAllowed || false,
        startDate: startDate || '',
        endDate: endDate || '',
        totalSites: totalSites || 0,
        vendorCode: vendorCode || '',
        vendorType: vendorType || '',
        consumers: consumers || [],
        provider: provider || null,
        isConsumer: isConsumer || false,
        isProvider: isProvider || false,
        providerDetails: providerDetails || null,
      }));
    }
  };

  const handleVendorSelection = async (selectedVendor) => {
    if (!selectedVendor || !selectedVendor.rowKey) {
      console.error('Invalid vendor selected:', selectedVendor);
      return;
    }
    try {
      const providerData = await getVendor(selectedVendor.rowKey);
      setForm((prevForm) => ({
        ...prevForm,
        provider: selectedVendor.rowKey,
        providerDetails: providerData,
      }));
      setIsSaved(false); // Reset isSaved when vendor selection changes
    } catch (error) {
      console.error('Failed to fetch provider details:', error);
      setForm((prevForm) => ({
        ...prevForm,
        provider: null,
        providerDetails: null,
      }));
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setForm((prevForm) => ({
      ...prevForm,
      provider: null,
      providerDetails: null,
    }));
    setIsSaved(false); // Reset isSaved when editing provider
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (name === 'dualVendor') {
      setForm((prevForm) => {
        let updatedProvider = prevForm.provider;
        let updatedConsumers = prevForm.consumers;
        let updatedIsConsumer = false;
        let updatedIsProvider = false;
        if (value === 'Consumer') {
          updatedIsConsumer = true;
          updatedConsumers = null;
        } else if (value === 'Provider') {
          updatedIsProvider = true;
          updatedConsumers = [];
          updatedProvider = prevForm.isConsumer ? 0 : updatedProvider;
        } else if (value === 'None') {
          updatedConsumers = null;
          updatedProvider = null;
        }
        return {
          ...prevForm,
          isConsumer: updatedIsConsumer,
          isProvider: updatedIsProvider,
          provider: updatedProvider,
          consumers: updatedConsumers,
        };
      });
    } else if (type === 'checkbox') {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: e.target.checked,
      }));
    } else if (name === 'reciprocalAllowed') {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: value === 'true',
      }));
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: value,
      }));
    }
    setIsSaved(false); // Reset isSaved when form changes
  };

  const handleStatusChange = (e) => {
    const statusIsActive = e.target.value === 'Active';
    const currentDate = new Date().toISOString().split('T')[0];

    setForm((prevForm) => ({
      ...prevForm,
      status: statusIsActive,
      startDate: statusIsActive && !prevForm.startDate ? currentDate : prevForm.startDate,
      endDate: !statusIsActive && !prevForm.endDate ? currentDate : prevForm.endDate,
    }));
    setIsSaved(false); // Reset isSaved when status changes
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormChanged) return;
    setShowSpinner(true);
    setIsSaved(false); // Reset before submission

    const submitData = {
      ...form,
      feedType: parseInt(form.feedType),
      vendorType: parseInt(form.vendorType),
      startDate: form.startDate === '1900-01-01' ? undefined : form.startDate,
      endDate: form.endDate === '1900-01-01' ? undefined : form.endDate,
      status: form.status,
    };

    try {
      let updatedVendor;
      if (vendor) {
        await updateVendor(vendor.rowKey, submitData);
        updatedVendor = vendor.rowKey;
      } else {
        updatedVendor = await createVendor(submitData);
      }

      const newVendorData = await getVendor(updatedVendor);
      setSelectedVendor(newVendorData);
      setForm({ ...newVendorData, startDate: convertToDateInputValue(newVendorData.startDate || currentDate), endDate: convertToDateInputValue(newVendorData.endDate || '') });
      initialFormRef.current = { ...newVendorData, startDate: convertToDateInputValue(newVendorData.startDate || currentDate), endDate: convertToDateInputValue(newVendorData.endDate || '') };
      setIsVendorLocked(true);
      setIsVendorCodeLocked(true);

      if (typeof onVendorDataChange === 'function') {
        onVendorDataChange(newVendorData);
      }
      setShowLocalSuccess(true);
      setLocalText('Vendor updated successfully');
      setShowSpinner(false);
      setIsSaved(true); // Set after successful save
      setIsFormChanged(false);
    } catch (error) {
      setShowLocalError(true);
      setLocalText(`An error occurred: ${error.message}`);
      setShowSpinner(false);
      setIsSaved(false); // Ensure isSaved is false on error
    }
  };

  const openDeleteModal = (e) => {
    e.preventDefault();
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleArchive = (e) => {
    e.preventDefault();
    if (vendor && vendor.rowKey) {
      archiveVendor(vendor.rowKey);
    }
  };

  useEffect(() => {
    const fetchTypes = async () => {
      const feedData = await getModalFeedTypes();
      const vendorData = await getModalVendorTypes();
      setVendorTypes(vendorData);
      setFeedTypes(feedData);
    };
    fetchTypes();
  }, []);

  useEffect(() => {
    if (!vendor) {
      setForm(initialState);
      initialFormRef.current = initialState;
      setIsSaved(false); // Reset isSaved for new vendor
    } else {
      setForm({
        ...initialState,
        providerDetails: vendor.providerDetails || null,
      });
      initialFormRef.current = {
        ...initialState,
        providerDetails: vendor.providerDetails || null,
      };
      setIsSaved(false); // Reset isSaved when vendor changes
    }
  }, [vendor]);

  const startSpinner = () => {
    setShowSpinner(true);
  };

  const convertToDateInputValue = (dateStr) => {
    const date = new Date(dateStr);
    if (!isNaN(date)) {
      return date.toISOString().split('T')[0];
    }
    return '';
  };

  const handleVendorInputClick = async (e) => {
    e.preventDefault();
    if (vendorLinkDisabled) return;
    if (vendor && vendor.provider) {
      const vendorData = await getVendor(vendor.provider);
      handleVendorClick(vendor.provider);
      setIsProviderOpen(true);
      setSelectedVendor(vendorData);
    }
  };

  const closeVendorProviderModal = () => {
    setIsProviderOpen(false);
  };

  useEffect(() => {
    if (showSpinner) {
      const timer = setTimeout(() => {
        setShowSpinner(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showSpinner]);

  useEffect(() => {
    if (selectedVendor) {
      const formattedStartDate = selectedVendor.startDate
        ? convertToDateInputValue(selectedVendor.startDate)
        : '';
      const formattedEndDate = selectedVendor.endDate
        ? convertToDateInputValue(selectedVendor.endDate)
        : '';

      const newFormState = {
        delayProfile: selectedVendor.delayProfile || 0,
        activeSites: selectedVendor.activeSites || 0,
        feedType: selectedVendor.feedType || '',
        hasReciprocal: selectedVendor.hasReciprocal || false,
        inactiveSites: selectedVendor.inactiveSites || 0,
        licensee: selectedVendor.licensee || '',
        rowKey: selectedVendor.rowKey || undefined,
        status: selectedVendor.status !== undefined ? selectedVendor.status : true,
        reciprocalAllowed: selectedVendor.reciprocalAllowed || false,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        totalSites: selectedVendor.totalSites || 0,
        vendorCode: selectedVendor.vendorCode || '',
        vendorType: selectedVendor.vendorType || '',
        provider: selectedVendor.provider || form.provider,
        providerDetails: selectedVendor.providerDetails || form.providerDetails,
        isConsumer: selectedVendor.isConsumer || false,
        isProvider: selectedVendor.isProvider || false,
      };

      setForm(newFormState);
      initialFormRef.current = newFormState;
      setIsSaved(false); // Reset isSaved when selectedVendor changes
    }
  }, [selectedVendor]);

  const linkedAccountsText =
    linkedGroup.length > 1
      ? `(${linkedGroup.length - 1} associated accounts)`
      : '';

  useEffect(() => {
    async function fetchProviderDetails() {
      if (form.provider) {
        try {
          const providerData = await getVendor(form.provider);
          setForm((prevForm) => ({
            ...prevForm,
            providerDetails: providerData,
          }));
          setLinkedGroup(linkedGroup);
        } catch (error) {
          console.error('Failed to fetch provider details:', error);
        }
      }
    }

    fetchProviderDetails();
  }, [form.provider, setLinkedGroup]);

  return (
    <div className="mt-8 flex flex-wrap justify-around bg-white dark:bg-stone-900 sm:flex-nowrap">
      <div className="w-full sm:w-full lg:ml-4 lg:w-2/3 xl:w-2/3">
        <div className="mt-3 w-full overflow-y-auto rounded-lg bg-white pl-2 pt-[2px] dark:bg-stone-900 sm:max-h-full">
          <div
            className="mx-auto mt-5 min-w-0 sm:mt-0"
            style={{ maxHeight: '575px', overflowY: 'auto' }}
          >
            <form
              onSubmit={handleSubmit}
              className="ml-3 mt-1.5 w-full overflow-x-hidden sm:w-full sm:min-w-[150px] md:w-5/6 md:min-w-[200px] lg:w-2/3 lg:min-w-[550px] xlg:min-w-[700px]"
            >
              <div className="space-y-4">
                <div className="-mx-2 flex flex-col lg:flex-row">
                  <div className="mb-6 w-full px-2 md:mb-0">
                    <label className="block">
                      <span className="label">
                        Vendor
                        <small className="text-gray-300">{linkedAccountsText}</small>
                      </span>
                      {initialLoading ? (
                        <WaveformLoaderVendors
                          isLoading={initialLoading}
                          darkMode={darkMode}
                          marginTop="0.1rem"
                          width="100%"
                          height="2.4rem"
                        />
                      ) : (
                        <div className="relative">
                          <input
                            className="mt-0.5 block w-full rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 pr-8 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300 xs:w-2/5 sm:w-4/5 md:w-full"
                            type="text"
                            maxLength="100"
                            name="licensee"
                            placeholder="Vendor Name"
                            value={form.licensee}
                            onChange={handleChange}
                            disabled={isVendorLocked}
                            title={isVendorLocked ? "Click the lock icon to unlock and make alterations" : ""}
                          />
                          <button
                            type="button"
                            onClick={() => setIsVendorLocked(!isVendorLocked)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 focus:outline-none"
                          >
                            {isVendorLocked ? (
                              <FiLock className="h-4 w-4 text-gray-500 dark:text-gray-200" />
                            ) : (
                              <FiUnlock className="h-4 w-4 text-gray-500 dark:text-gray-200" />
                            )}
                          </button>
                        </div>
                      )}
                    </label>
                  </div>
                  <div className="w-full px-2 md:mt-3 md:w-full lg:mt-0 lg:w-2/5">
                    <label className="block">
                      <span className="label">Vendor Code</span>
                      {initialLoading ? (
                        <WaveformLoaderVendors
                          isLoading={initialLoading}
                          darkMode={darkMode}
                          marginTop="0.1rem"
                          width="100%"
                          height="2.4rem"
                        />
                      ) : (
                        <div className="relative">
                          <input
                            className="mt-0.5 block w-full rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 pr-8 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300 xs:w-2/5 sm:w-4/5 md:w-full"
                            type="text"
                            name="vendorCode"
                            placeholder="Code"
                            value={form.vendorCode}
                            onChange={handleChange}
                            maxLength="5"
                            autoComplete="off"
                            disabled={isVendorCodeLocked}
                            title={isVendorCodeLocked ? "Click the lock icon to unlock and make alterations" : ""}
                          />
                          <button
                            type="button"
                            onClick={() => setIsVendorCodeLocked(!isVendorCodeLocked)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 focus:outline-none"
                          >
                            {isVendorCodeLocked ? (
                              <FiLock className="h-4 w-4 text-gray-500 dark:text-gray-200" />
                            ) : (
                              <FiUnlock className="h-4 w-4 text-gray-500 dark:text-gray-200" />
                            )}
                          </button>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
                <div className="flex flex-col justify-between lg:flex-row">
                  <div className="flex w-full flex-col justify-between lg:flex-row">
                    <label className="inline-block flex-1">
                      <span className="label">Status</span>
                      {initialLoading ? (
                        <WaveformLoaderVendors
                          isLoading={initialLoading}
                          darkMode={darkMode}
                          marginTop="0.1rem"
                          width="100%"
                          height="2.45rem"
                        />
                      ) : (
                        <select
                          className="split-dropdown w-full xs:w-2/5 sm:w-4/5 md:w-full"
                          name="status"
                          value={form.status ? 'Active' : 'Inactive'}
                          onChange={handleStatusChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      )}
                    </label>
                    <label className="ml-2 mt-0 inline-block flex-1 xs:ml-0 xs:mt-3 md:ml-0 md:mt-3.5 lg:ml-4 lg:mt-0">
                      <span className="label">Allow Reciprocal</span>
                      {initialLoading ? (
                        <WaveformLoaderVendors
                          isLoading={initialLoading}
                          darkMode={darkMode}
                          marginTop="0.1rem"
                          width="100%"
                          height="2.45rem"
                        />
                      ) : (
                        <select
                          className="split-dropdown w-full xs:w-2/5 sm:w-4/5 md:w-full"
                          name="reciprocalAllowed"
                          value={form.reciprocalAllowed ? 'true' : 'false'}
                          onChange={handleChange}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      )}
                    </label>
                  </div>
                </div>
                {form.rowKey && (
                  <div className="-mx-2 flex flex-wrap">
                    <div
                      className={`w-full px-2 ${form.isConsumer ? 'lg:w-1/3' : 'w-full'}`}
                    >
                      <label className="block">
                        <span className="label">Dual Vendor</span>
                        {initialLoading ? (
                          <WaveformLoaderVendors
                            isLoading={initialLoading}
                            darkMode={darkMode}
                            marginTop="0.1rem"
                            width="100%"
                            height="2.45rem"
                          />
                        ) : (
                          <select
                            className="dropdown w-full xs:w-2/5 sm:w-4/5 md:w-full"
                            name="dualVendor"
                            value={
                              form.isConsumer
                                ? 'Consumer'
                                : form.isProvider
                                  ? 'Provider'
                                  : 'None'
                            }
                            onChange={handleChange}
                          >
                            <option value="None">None</option>
                            <option value="Consumer">Consumer</option>
                            <option value="Provider">Provider</option>
                          </select>
                        )}
                      </label>
                    </div>
                    {form.isConsumer && (
                      <div className="w-full px-2 xs:mt-3 lg:mt-0 lg:w-2/3">
                        <label className="block">
                          {form.providerDetails ? (
                            <span className="label">Provider</span>
                          ) : (
                            <span className="label">Provider Search</span>
                          )}
                          {initialLoading ? (
                            <WaveformLoaderVendors
                              isLoading={initialLoading}
                              darkMode={darkMode}
                              marginTop="0.1rem"
                              width="100%"
                              height="2.45rem"
                            />
                          ) : form.providerDetails ? (
                            <div
                              className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 font-semibold text-gray-600 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                            >
                              <span
                                className="block max-w-[rezz150px] overflow-hidden truncate text-ellipsis whitespace-nowrap"
                                onClick={handleVendorInputClick}
                                title={form.providerDetails.licensee}
                              >
                                {form.providerDetails.licensee}
                              </span>
                              <FaEdit
                                className="ml-2 cursor-pointer"
                                onClick={handleEditClick}
                              />
                            </div>
                          ) : (
                            <DualVendorSearch
                              excludeVendorId={form.rowKey}
                              selectedVendor={form.provider}
                              excludeInactive={true}
                              onVendorSelect={handleVendorSelection}
                              excludeConsumers={true}
                            />
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                )}
                <div className="-mx-2 flex flex-wrap">
                  <div className="w-full px-2">
                    <label className="block">
                      <span className="label">Feed Type</span>
                      {initialLoading ? (
                        <WaveformLoaderVendors
                          isLoading={initialLoading}
                          darkMode={darkMode}
                          marginTop="0.1rem"
                          width="100%"
                          height="2.45rem"
                        />
                      ) : (
                        <select
                          className="dropdown w-full xs:w-2/5 sm:w-4/5 md:w-full"
                          name="feedType"
                          value={form.feedType}
                          onChange={handleChange}
                        >
                          <option value="">Select</option>
                          {Array.isArray(feedTypes) &&
                            feedTypes.map((feedType) => (
                              <option key={feedType.id} value={feedType.id}>
                                {feedType.type}
                              </option>
                            ))}
                        </select>
                      )}
                    </label>
                  </div>
                  <div className="mt-3 w-full px-2 md:mt-3.5">
                    <label className="block">
                      <span className="label">Vendor Type</span>
                      {initialLoading ? (
                        <WaveformLoaderVendors
                          isLoading={initialLoading}
                          darkMode={darkMode}
                          marginTop="0.1rem"
                          width="100%"
                          height="2.45rem"
                        />
                      ) : (
                        <select
                          className="dropdown w-full xs:w-2/5 sm:w-4/5 md:w-full"
                          name="vendorType"
                          value={form.vendorType}
                          onChange={handleChange}
                        >
                          <option value="">Select</option>
                          {Array.isArray(vendorTypes) &&
                            vendorTypes.map((vendorType) => (
                              <option key={vendorType.id} value={vendorType.id}>
                                {vendorType.type}
                              </option>
                            ))}
                        </select>
                      )}
                    </label>
                  </div>
                </div>
                <div className="-mx-2 flex flex-col justify-between lg:flex-row">
                  <div className="w-full px-2">
                    <label className="block">
                      <span className="label">Start Date</span>
                      {initialLoading ? (
                        <WaveformLoaderVendors
                          isLoading={initialLoading}
                          darkMode={darkMode}
                          marginTop="0.1rem"
                          width="100%"
                          height="2.6rem"
                        />
                      ) : (
                        <input
                          className="split-dropdown w-full xs:w-2/5 sm:w-4/5 md:w-full"
                          type="date"
                          name="startDate"
                          value={form.startDate}
                          onChange={handleChange}
                        />
                      )}
                    </label>
                  </div>
                  <div className="w-full px-2 xs:mt-3 md:mt-3.5 lg:mt-0">
                    <label className="block">
                      <span className="label">End Date</span>
                      {initialLoading ? (
                        <WaveformLoaderVendors
                          isLoading={initialLoading}
                          darkMode={darkMode}
                          marginTop="0.1rem"
                          width="100%"
                          height="2.6rem"
                        />
                      ) : (
                        <input
                          className="split-dropdown w-full xs:w-2/5 sm:w-4/5 md:w-full"
                          type="date"
                          name="endDate"
                          value={form.endDate}
                          onChange={handleChange}
                        />
                      )}
                    </label>
                  </div>
                </div>
              </div>
              <div className="my-5 flex flex-col xs:flex-row sm:items-center sm:justify-start">
                <button
                  type="submit"
                  className={`primary ${!isFormChanged ? 'cursor-not-allowed opacity-50' : ''}`}
                  disabled={!isFormChanged}
                  onClick={startSpinner}
                >
                  {showSpinner ? (
                    <div className="mr-2.5 inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-gray-300" />
                  ) : (
                    <img
                      src={saveIcon}
                      alt="Save"
                      className="mr-1.5 mt-1 inline-block h-4 w-4"
                    />
                  )}
                  {showSpinner ? 'Saving' : isSaved ? 'Saved' : form.rowKey ? 'Update' : 'Create'}
                </button>
                {form.rowKey && (
                  <button
                    type="button"
                    onClick={openDeleteModal}
                    className="danger ml-3 xs:ml-0 xs:ml-3"
                  >
                    <img
                      src={archiveIcon}
                      alt="Archive"
                      className="mr-1.5 inline-block h-5 w-5"
                    />
                    Archive
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      {form.rowKey && (
        <div
          className={`mr-3 mt-0 block max-h-[50vh] w-full overflow-y-auto xs:hidden sm:mt-0 sm:block sm:max-h-full sm:w-full sm:overflow-y-visible md:ml-[0px] md:block md:w-full lg:block lg:w-full lg:min-w-[400px] xl:w-full`}
        >
          <CommentTable VendorID={vendor ? vendor.rowKey : null} />
        </div>
      )}
      {showDeleteModal && (
        <DeleteModal
          title="Are you sure?"
          onCancel={() => closeDeleteModal(false)}
          onDelete={handleArchive}
        />
      )}
      {isProviderOpen && (
        <VendorModal
          onClose={closeVendorProviderModal}
          isOpen={isProviderOpen}
          vendor={selectedVendor}
        >
          {selectedVendor && <VendorForm vendor={selectedVendor} />}
          <VendorFiles
            vendorId={selectedVendor ? selectedVendor.rowKey : null}
          />
        </VendorModal>
      )}
      {showLocalSuccess && (
        <SuccessNotification
          show={showLocalSuccess}
          onClose={closeNotification}
          text={localText}
        />
      )}
      {showLocalError && (
        <ErrorNotification
          show={showLocalError}
          onClose={closeNotification}
          text={localText}
        />
      )}
      {callVendorForRefresh && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center pt-8 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-stone-950 bg-opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:h-screen sm:align-middle"
              aria-hidden="true"
            >
            </span>
            <div
              className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all dark:bg-stone-800 sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
            >
              <div className="flex h-full flex-col">
                <div className="flex flex-grow items-center justify-center bg-white px-4 py-6 dark:bg-stone-800 sm:p-6">
                  <div className="sm:flex sm:items-center">
                    <div className="text-center sm:text-left">
                      <h3
                        className="pt-6 text-lg font-semibold leading-6 text-gray-900 dark:text-gray-200"
                        id="modal-headline"
                      >
                        Changes have been made, please refresh.
                      </h3>
                    </div>
                  </div>
                </div>
                <div
                  className="justify-center bg-white px-4 py-3 dark:bg-stone-800 sm:flex sm:flex-row-reverse sm:px-6"
                >
                  <button
                    onClick={handleRefreshYes}
                    type="button"
                    className="mt-3 inline-flex w-28 justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none dark:bg-indigo-500 dark:hover:bg-indigo-600 sm:ml-2 sm:mt-0 sm:text-sm"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}